<script setup lang="ts">
import { useExtendedI18n } from '@/i18n'
import { useVuelidate } from '@vuelidate/core'
import { required, email } from '@vuelidate/validators'
import { capitalize, get } from 'lodash'
import { onMounted, ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { safeFormat } from '@/modules/shared/utils/date'
import { rails_url } from '@/modules/shared/utils/rails'
import { unflattenKey } from '@/modules/shared/utils/v-table'
import {
  ActionItem,
  ActionsGroup,
  ActionsMenu,
  RouterLinkBack,
  VAvatar,
  VButton,
  VButtonInvisible,
  VIcon,
  VMailTo,
  VSection,
  VTable,
} from '@/modules/shared/components'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import TheLayout from '@/modules/shared/layouts/the-layout.vue'
import { VModal, VTextField } from '@/modules/shared/components'
import { useModal } from '@/modules/shared/composables/use-modal'
import ScreeningCheckWrapper from '@/modules/screening/component/CheckWrapper.vue'
import { Status } from '@/modules/screening/types'
import { useWorkspaceStore } from '@/modules/workspace/stores/workspace-store'
import IndividualsNav from '../components/individuals-nav.vue'
import { useInvestingInvestorStore } from '../stores/investor-store'
import { addressColumns } from '../config/columns'
import AccreditationBadge from '@/modules/screening/component/AccreditationBadge.vue'
import ScreeningAccreditationWrapper from '@/modules/screening/component/AccreditationWrapper.vue'
import { useAccreditationStore } from '@/modules/screening/stores/accreditation-store'
import { useIndividualStore } from '../stores/better-individual-store'

///////////////////////////////////////////////////////////////////////////////
//
///////////////////////////////////////////////////////////////////////////////

const { t } = useExtendedI18n()

///////////////////////////////////////////////////////////////////////////////

const route = useRoute()
const router = useRouter()

const authStore = useAuthStore()
const workspaceStore = useWorkspaceStore()
const accreditationStore = useAccreditationStore()
const current_user = computed(() => authStore.current_user)
const cid = computed(() => `individual:${current_user.value.investor_id}`)
const investingInvestorStore = useInvestingInvestorStore()
const individualStore = useIndividualStore()

const isAdmin = computed(() => current_user.value.role !== 'investor')

const skeleton = ref(true)
const selectedStatus = ref('active')

const deleteAction = async (item) => {
  if (!window.confirm('Are you sure?')) return
  await investingInvestorStore.removeIndividual(item.id)
  await investingInvestorStore.fetchIndividuals('all')
}

const revokeAccessAction = async (item) => {
  if (!window.confirm('Are you sure?')) return
  await investingInvestorStore.revokeAccessIndividual(item.id, 'all')
}

const activateAccessAction = async (item) => {
  if (!window.confirm('Are you sure?')) return
  await investingInvestorStore.activateAccessIndividual(item.id, 'all')
}

const sendRegistrationInvitation = async (item) => {
  await individualStore.resendInvitation(item.id, { slug: route.params.slug as string })
  alert(`${item.name} has been sent a link to register`)
}

const can_view_investor_profile = (item) => {
  // Backend is already handling the permissions on which investors that other investors can see. So in this case, the investor should be able to access the investors that being displayed here
  // if (isAdmin.value) return true
  // if (current_user.value.investor_id === item.id) return true
  // if (!workspaceStore.current.options.hide_investors_from_investors) return true
  return true
}

///////////////////////////////////////////////////////////////////////////////
// Add Individual
///////////////////////////////////////////////////////////////////////////////
const individualFormModal = useModal()

const individual_initial_state = {
  id: null,
  first_name: null,
  last_name: null,
  email: null,
}

const individual_rules = {
  first_name: { required },
  last_name: { required },
  email: { required, email },
}
const individual_form = ref({ ...individual_initial_state })
const vi$ = useVuelidate(individual_rules, individual_form, { $lazy: true })

const addIndividual = () => {
  vi$.value.$reset()
  individual_form.value = { ...individual_initial_state }
  individualFormModal.open()
}

const saveIndividual = async () => {
  const valid = await vi$.value.$validate()
  if (!valid) return

  await individualStore.addIndividual(individual_form.value, 'all', { slug: route.params.slug as string })
  individualFormModal.close()
  investingInvestorStore.fetchIndividuals('all')
}

///////////////////////////////////////////////////////////////////////////////
// Table
///////////////////////////////////////////////////////////////////////////////
const isAdminIndividual = (individual) => {
  const roles = unflattenKey(individual, 'roles') || []
  return roles.some((r) => r.group === workspaceStore.current.name && r.role === 'Group Admin')
}

const individuals = computed(() => {
  if (isAdmin.value) {
    return investingInvestorStore.individuals
      .filter((individual) => !isAdminIndividual(individual))
      .filter(
        (individual) =>
          individual?.status?.find((a) => a.group_id === workspaceStore.current.id)?.status?.toLowerCase() ===
          selectedStatus.value,
      )
  }

  return investingInvestorStore.individuals
})

const canViewColumn = (column) => {
  if (column.key === 'actions') return true
  if (column.key === 'screening_check_status') return workspaceStore.current.options.is_aml_enabled
  if (authStore.is_site_or_group_admin) return true
  if (['email', 'name'].includes(column.key)) return true

  return false
}

const columns = computed(() => {
  const cols = [
    {
      key: 'name',
      name: capitalize(t('shared.name', 0)),
      type: 'string',
      is_visible: true,
      fixed: true,
    },
    {
      key: 'birthdate',
      name: 'Birthdate',
      type: 'date',
      is_visible: false,
    },
    {
      key: 'phone',
      name: 'Phone',
      type: 'string',
      is_visible: false,
    },
    {
      key: 'investing_name',
      name: capitalize(t('shared.investing name')),
      type: 'string',
      is_visible: !isAdmin.value,
    },
    {
      key: 'screening_check_status',
      name: capitalize(t('shared.AML status')),
      type: 'string',
      is_visible: true,
    },
    {
      key: 'screening_accreditation_status',
      name: capitalize(t('Accreditation status')),
      type: 'string',
      is_visible: true,
    },
    {
      key: 'subgroups',
      name: capitalize(t('shared.subgroup')),
      type: 'string',
      is_visible: false,
    },
    {
      key: 'email',
      name: capitalize(t('shared.email')),
      type: 'string',
      is_visible: true,
    },
    {
      key: 'roles',
      name: capitalize(t('shared.role')),
      type: 'string',
      align: 'left',
      is_visible: false,
    },
    {
      key: 'last_login',
      name: capitalize(t('shared.last login at')),
      type: 'date',
      is_visible: true,
    },
    {
      key: 'created_at',
      name: capitalize(t('shared.date added')),
      type: 'date',
      is_visible: false,
    },
    {
      key: 'membership_currency',
      name: capitalize(t('shared.preferred currency')),
      type: 'string',
      is_visible: false,
    },
    ...addressColumns(),
    {
      key: 'type',
      name: capitalize(t('shared.type')),
      type: 'string',
      is_visible: false,
    },
    {
      key: 'tax_id',
      name: t('shared.SSN / Tax ID'),
      type: 'string',
      is_visible: false,
    },
    {
      key: 'status',
      name: capitalize(t('shared.status')),
      type: 'string',
      is_visible: false,
    },
    {
      key: 'disbursement_method_confirmed_at',
      name: 'Payment Method Confirmed At',
      type: 'string',
      align: 'left',
      is_visible: false,
    },
    {
      key: 'disbursement_wire_bank_name',
      name: 'Bank Name',
      type: 'string',
      align: 'left',
      is_visible: false,
    },
    {
      key: 'disbursement_wire_bank_address',
      name: 'Bank Address',
      type: 'string',
      align: 'left',
      is_visible: false,
    },
    {
      key: 'disbursement_wire_bank_routing_number',
      name: 'Bank Routing Number',
      type: 'string',
      align: 'left',
      is_visible: false,
    },
    {
      key: 'disbursement_wire_bank_account_number',
      name: 'Bank Account Number',
      type: 'string',
      align: 'left',
      is_visible: false,
    },
    {
      key: 'disbursement_wire_bank_swift_code',
      name: 'Bank Swift Code',
      type: 'string',
      align: 'left',
      is_visible: false,
    },
    {
      key: 'disbursement_wire_account_name',
      name: 'Account Name',
      type: 'string',
      align: 'left',
      is_visible: false,
    },
    {
      key: 'disbursement_wire_for_further_credit_to',
      name: 'For Further Credit To',
      type: 'string',
      align: 'left',
      is_visible: false,
    },
    {
      key: 'disbursement_wire_bank_notes',
      name: 'Additional Banking Details',
      type: 'string',
      align: 'left',
      is_visible: false,
    },
    {
      key: 'disbursement_check_receiver_name',
      name: 'Check Receiver Name',
      type: 'string',
      align: 'left',
      is_visible: false,
    },
    {
      key: 'disbursement_other_details',
      name: 'Other Payment Method Details',
      type: 'string',
      align: 'left',
      is_visible: false,
    },
    {
      key: 'actions',
      name: '',
      type: 'actions',
      is_visible: isAdmin.value,
    },
  ]
  return cols.filter((column) => canViewColumn(column))
})

const getStatus = (item) => {
  const statuses = unflattenKey(item, 'status')
  return statuses.find((status) => status.group_id === workspaceStore.current.id)?.status
}

const canEditAccreditation = (item) => {
  if (!workspaceStore.current.options.is_accreditation_required) return false
  if (current_user.value.investor_id?.toString() === item.id.toString()) return true

  return false
}

const accreditationCallback = (item) => {
  const accreditation = accreditationStore.screeningAccreditation
  const individual = investingInvestorStore.individuals.find((ind) => ind.id === item.id)
  investingInvestorStore.items.set(individual._cid, {
    ...individual,
    screening_accreditation_status: accreditation.status,
    screening_accreditation_id: accreditation.id,
  })
}

onMounted(async () => {
  await workspaceStore.fetchCurrent()

  if (isAdmin.value) {
    await investingInvestorStore.fetchIndividuals('all')
  } else {
    await investingInvestorStore.fetchIndividuals(cid.value)
  }
  skeleton.value = false
})
</script>

<template>
  <TheLayout>
    <div class="-mt-8 mb-6 flex items-center justify-between">
      <div>
        <RouterLinkBack class="text-lg" :to="{ name: 'dashboard' }">
          <VButtonInvisible>← {{ capitalize(t('shared.back')) }}</VButtonInvisible>
        </RouterLinkBack>
      </div>
    </div>

    <VSection :label="isAdmin ? null : 'Individuals'">
      <IndividualsNav v-if="isAdmin" />
      <div class="mb-3 flex items-center justify-between" v-if="isAdmin">
        <div class="flex items-center space-x-1.5">
          <VButton :active="selectedStatus === 'active'" class="w-32" :click="() => (selectedStatus = 'active')">
            <span>{{ capitalize(t('shared.active')) }}</span>
          </VButton>
          <VButton :active="selectedStatus === 'pending'" class="w-32" :click="() => (selectedStatus = 'pending')">
            <span>{{ capitalize(t('shared.pending')) }}</span>
          </VButton>
          <VButton :active="selectedStatus === 'inactive'" class="w-32" :click="() => (selectedStatus = 'inactive')">
            <span>{{ capitalize(t('shared.inactive')) }}</span>
          </VButton>
        </div>
        <div v-if="authStore.is_site_or_group_admin" class="flex items-center space-x-1.5">
          <VButton :click="addIndividual" size="md" variant="v-blue">
            <div class="mr-1 flex items-center space-x-2">
              <div><VIcon name="Plus" /></div>
              <div>Add Individual</div>
            </div>
          </VButton>
          <RouterLink :to="{ name: 'investing.individuals.import' }">
            <VButton>
              <div class="mr-1 flex items-center space-x-2">
                <div><VIcon name="CornerRightDown" /></div>
                <div>Import</div>
              </div>
            </VButton>
          </RouterLink>
        </div>
      </div>

      <VTable
        :name="`individuals-${route.params.slug}`"
        :columns="columns"
        :items="individuals"
        :skeleton="skeleton"
        :slots="[
          'actions',
          'screening_check_status',
          'screening_accreditation_status',
          'email',
          'last_login',
          'name',
          'roles',
          'subgroups',
        ]"
      >
        <template #name="{ item }">
          <RouterLink
            v-if="can_view_investor_profile(item)"
            class="flex cursor-pointer items-center space-x-3"
            :to="{
              name: 'investing.individual-overview',
              params: { individual_id: item.id },
            }"
          >
            <VAvatar :image="item.profile_picture" :initials="item.initials" size="sm" />
            <div class="hyperlink -mt-0.5">
              {{ item.name }}
            </div>
          </RouterLink>
          <div class="flex items-center space-x-3" v-else>
            <VAvatar :image="item.profile_picture" :initials="item.initials" size="sm" />
            <div class="-mt-0.5">
              {{ item.name }}
            </div>
          </div>
        </template>
        <template #screening_accreditation_status="{ item }">
          <ScreeningAccreditationWrapper
            v-if="item?.screening_accreditation_id"
            :screening_accreditation_id="item?.screening_accreditation_id"
            :status="item?.screening_accreditation_status"
            :can_edit="canEditAccreditation(item)"
            :callback="() => accreditationCallback(item)"
          />
        </template>
        <template #screening_check_status="{ item }">
          <ScreeningCheckWrapper
            v-if="item?.screening_check_id"
            :checkId="item?.screening_check_id"
            :status="item?.screening_check_status as Status"
          />
        </template>
        <template #email="{ item }">
          <VMailTo :email="item.email" />
        </template>
        <template #last_login="{ item }">
          {{ safeFormat(item.last_login, "EEEE, MMMM d, y 'at' h:mma", 'Never') }}
        </template>
        <template #roles="{ item }">
          <span v-for="role in unflattenKey(item, 'roles')">
            <span>{{ role.group }} - {{ role.role }}<br /></span>
          </span>
        </template>
        <template #subgroups="{ item }">
          <span v-for="(subgroup, index) in unflattenKey(item, 'subgroups')" :key="subgroup.id" style="display: block">
            <RouterLink
              v-if="isAdmin"
              class="cursor-pointer whitespace-break-spaces font-medium text-[#51889B] underline decoration-[#51889B]/50"
              :to="{
                name: 'investing.subgroup-overview',
                params: { subgroup_id: subgroup.id },
              }"
            >
              {{ subgroup.name }}
            </RouterLink>
            <span class="text-gray-900" v-else>{{ subgroup.name }}</span>
          </span>
        </template>
        <template #actions="{ item }">
          <div class="flex items-center justify-end space-x-1.5">
            <ActionsMenu v-if="authStore.is_site_or_group_admin">
              <ActionsGroup>
                <ActionItem
                  @click="
                    () => router.push({ name: 'investing.individual.account', params: { individual_id: item.id } })
                  "
                  icon="LinkExternal"
                  :text="capitalize(t('shared.edit profile'))"
                  tag="a"
                />
                <ActionItem
                  v-if="
                    authStore.is_site_or_group_admin && (getStatus(item) == 'Active' || getStatus(item) == 'Pending')
                  "
                  icon="FileX"
                  :text="capitalize(t('shared.revoke access'))"
                  @click="revokeAccessAction(item)"
                />
                <ActionItem
                  v-if="getStatus(item) !== 'Active' && authStore.is_site_or_group_admin"
                  icon="ThumbsUp"
                  :text="capitalize(t('shared.activate'))"
                  @click="activateAccessAction(item)"
                />
                <ActionItem
                  v-if="authStore.is_site_admin"
                  icon="Glasses02"
                  :text="capitalize(t('shared.view as'))"
                  @click="authStore.viewAs(get(item, 'user.id'))"
                />
                <ActionItem
                  v-if="authStore.is_site_admin"
                  icon="LinkExternal"
                  tag="a"
                  :text="capitalize(t('shared.merge'))"
                  :href="`${rails_url()}/investors/${get(item, 'id')}/merge`"
                />

                <ActionItem
                  v-if="authStore.is_site_or_group_admin && !unflattenKey(item, 'user')"
                  icon="FileCheck"
                  tag="a"
                  :text="capitalize(t('shared.resend invitation email'))"
                  :disabled="workspaceStore.current.setup_mode"
                  :tooltip="'Not available in setup mode'"
                  @click="sendRegistrationInvitation(item)"
                />
              </ActionsGroup>
              <ActionsGroup>
                <ActionItem
                  v-if="authStore.is_site_or_group_admin"
                  icon="Trash"
                  :text="capitalize(t('shared.delete'))"
                  @click="() => deleteAction(item)"
                />
              </ActionsGroup>
            </ActionsMenu>
          </div>
        </template>
      </VTable>
    </VSection>
    <VModal :modalStore="individualFormModal">
      <template #main>
        <VSection label="Add an Individual">
          <div class="space-y-3">
            <VTextField
              v-model="individual_form.first_name"
              :label="capitalize(t('shared.first name'))"
              property="first_name"
              :v$="vi$"
            />
            <VTextField
              v-model="individual_form.last_name"
              :label="capitalize(t('shared.last name'))"
              property="last_name"
              :v$="vi$"
            />
            <VTextField
              v-model="individual_form.email"
              :label="capitalize(t('shared.email'))"
              property="email"
              type="email"
              :v$="vi$"
            />
          </div>
        </VSection>
      </template>
      <template #footer>
        <VButton :click="saveIndividual" variant="primary" size="lg" class="w-full">Add Individual</VButton>
      </template>
    </VModal>
  </TheLayout>
</template>
